import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MdLogout, MdMenu, MdSettings } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import NavbarClock from '../components/Navbar/NavbarClock';
import Notifications from '../components/Navbar/Notifications';
import QuickActions from '../components/Navbar/QuickActions';
import { useAuth } from '../hooks/useAuth';
import { useGetSettings } from '../hooks/useGetSettings';
import { renderFile } from '../utils/utils';

const Navbar = ({ onMenuClick }) => {
    const { data: showLogo } = useGetSettings(['company_logo', 'company_favIcon', 'setting_app_name']);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { signout } = useAuth();

    // Click-outside handler for profile dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        signout();
    };

    const userData = JSON.parse(localStorage.getItem('user')) || {};
    const userRole = userData?.role_name || 'User';

    return (
        <>
            <Helmet>
                <title>{showLogo?.setting_app_name || 'HRMS'}</title>
                <link
                    rel="icon"
                    href={
                        showLogo?.company_favIcon
                            ? renderFile(showLogo?.company_favIcon)
                            : '/logo192.png'
                    }
                />
            </Helmet>

            <Box
                component="header"
                sx={{
                    height: '4rem',
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '1rem',
                    '@media (min-width:1024px)': { px: '2rem' },
                    zIndex: 1100,
                    position: 'sticky',
                    top: 0,
                }}
            >
                {/* Left Section - Menu Button & Search */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem', '@media (min-width:768px)': { gap: '1rem' } }}>
                    <Box
                        component="button"
                        onClick={onMenuClick}
                        aria-label="Toggle menu"
                        sx={{
                            p: '0.5rem',
                            color: '#475569',
                            borderRadius: '0.5rem',
                            transition: 'background-color 150ms',
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            '&:hover': { bgcolor: '#f1f5f9' },
                        }}
                    >
                        <MdMenu size={24} />
                    </Box>

                    <QuickActions />
                </Box>

                {/* Right Section - Icons & Profile */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        '@media (min-width:768px)': { gap: '1.25rem' },
                    }}
                >
                    <NavbarClock />

                    {/* Notifications */}
                    <Notifications />

                    {/* Divider */}
                    <Box
                        sx={{
                            height: '2rem',
                            width: '1px',
                            bgcolor: '#e2e8f0',
                            display: 'none',
                            '@media (min-width:640px)': { display: 'block' },
                            mx: '0.25rem',
                        }}
                    />

                    {/* Profile Section */}
                    <Box sx={{ position: 'relative' }} ref={dropdownRef}>
                        <Box
                            component="button"
                            onClick={() => setShowProfileMenu(!showProfileMenu)}
                            aria-label="Profile menu"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                px: '0.5rem',
                                py: '0.375rem',
                                borderRadius: '0.5rem',
                                transition: 'background-color 150ms',
                                border: 'none',
                                bgcolor: 'transparent',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: '#f8fafc' },
                            }}
                        >
                            <Box
                                component="img"
                                src={`https://i.pravatar.cc/150?u=${userData?.email || 'user'}`}
                                alt="Profile"
                                sx={{
                                    width: '2rem',
                                    height: '2rem',
                                    borderRadius: '9999px',
                                    border: '2px solid #f1f5f9',
                                    objectFit: 'cover',
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'none',
                                    '@media (min-width:640px)': { display: 'flex' },
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    lineHeight: 1.25,
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}
                                >
                                    {userData?.name || 'User'}
                                </Box>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '10px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#64748b',
                                        fontWeight: 500,
                                    }}
                                >
                                    {userRole}
                                </Box>
                            </Box>
                        </Box>

                        {/* Dropdown Menu */}
                        {showProfileMenu && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    mt: '0.5rem',
                                    width: '12rem',
                                    bgcolor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0.75rem',
                                    boxShadow:
                                        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                                    zIndex: 1100,
                                    overflow: 'hidden',
                                    animation: 'navbarSlideDown 200ms ease-out',
                                    '@keyframes navbarSlideDown': {
                                        from: { opacity: 0, transform: 'translateY(-10px)' },
                                        to: { opacity: 1, transform: 'translateY(0)' },
                                    },
                                }}
                            >
                                <Box sx={{ p: '1rem', borderBottom: '1px solid #f1f5f9' }}>
                                    <Box
                                        component="p"
                                        sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a', m: 0 }}
                                    >
                                        {userData?.name || 'User'}
                                    </Box>
                                    <Box component="p" sx={{ fontSize: '0.75rem', color: '#64748b', m: 0 }}>
                                        {userData?.email || 'user@example.com'}
                                    </Box>
                                </Box>

                                <Box sx={{ p: '0.5rem' }}>
                                    <Box
                                        component="button"
                                        onClick={() => {
                                            navigate('/account-settings');
                                            setShowProfileMenu(false);
                                        }}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            px: '0.75rem',
                                            py: '0.625rem',
                                            fontSize: '0.875rem',
                                            color: '#334155',
                                            borderRadius: '0.5rem',
                                            transition: 'background-color 150ms',
                                            border: 'none',
                                            bgcolor: 'transparent',
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            '&:hover': { bgcolor: '#f8fafc' },
                                        }}
                                    >
                                        <MdSettings size={16} />
                                        Account Settings
                                    </Box>

                                    <Box
                                        component="button"
                                        onClick={handleLogout}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            px: '0.75rem',
                                            py: '0.625rem',
                                            fontSize: '0.875rem',
                                            color: '#e11d48',
                                            borderRadius: '0.5rem',
                                            transition: 'background-color 150ms',
                                            border: 'none',
                                            bgcolor: 'transparent',
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            '&:hover': { bgcolor: '#fff1f2' },
                                        }}
                                    >
                                        <MdLogout size={16} />
                                        Logout
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default Navbar;
